<template>
  <div class="dev-income">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>我的收益</h2>
      </div>

      <el-row :gutter="12" class="stat-row">
        <el-col :span="8">
          <div class="stat-card primary">
            <p class="stat-label">可提现余额（元）</p>
            <p class="stat-num">{{ income.incomeBalance || '0.00' }}</p>
            <div class="stat-actions">
              <el-button type="primary" size="small" @click="goWithdraw">去提现</el-button>
              <el-button type="success" size="small" plain @click="openTransfer">转入消费余额</el-button>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <p class="stat-label">冻结中收益（元）</p>
            <p class="stat-num warning">{{ income.incomeFrozen || '0.00' }}</p>
            <p class="stat-sub">
              共 {{ (stat && stat.frozenCount) || 0 }} 笔<template v-if="income.nextUnfreezeTime">，最早 {{ formatTime(income.nextUnfreezeTime) }} 解冻</template>
            </p>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-card">
            <p class="stat-label">累计收益（元）</p>
            <p class="stat-num">{{ income.totalIncome || '0.00' }}</p>
            <p class="stat-sub">已解冻 {{ (stat && stat.unfrozenSum ? (stat.unfrozenSum / 100).toFixed(2) : '0.00') }} 元</p>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="12" class="stat-row sub-row">
        <el-col :span="8">
          <div class="mini-card">本月收益：<b>{{ income.monthIncome || '0.00' }}</b> 元</div>
        </el-col>
        <el-col :span="8">
          <div class="mini-card">已提现：<b>{{ income.withdrawSum || '0.00' }}</b> 元（{{ (stat && stat.withdrawnCount) || 0 }} 笔）</div>
        </el-col>
        <el-col :span="8">
          <div class="mini-card">平台手续费：<b>{{ income.feeRateStr || '0%' }}</b></div>
        </el-col>
      </el-row>

      <div class="alert alert-info-light">
        插件被购买后，平台按订单实付金额收取 {{ income.feeRateStr || '0%' }} 手续费，剩余收益先进入「冻结中收益」，
        结算后冻结 {{ income.unfreezeDays || 0 }} 天（可在后台调整）自动转为「可提现余额」；每笔收益的具体解冻时间以明细中的「解冻时间」为准。
        提现需先绑定收款账户并提交申请，平台审核后线下打款。
        也可将可提现收益「转入消费余额」用于购买插件；转入的消费余额不可提现、不可转回，请谨慎操作。
      </div>

      <div class="filter-bar">
        <el-select v-model="queryParams.type" placeholder="全部变动类型" clearable size="small" style="width: 180px" @change="handleQuery">
          <el-option label="订单收益" value="1" />
          <el-option label="冻结解冻" value="2" />
          <el-option label="提现申请" value="3" />
          <el-option label="提现完成" value="4" />
          <el-option label="提现退回" value="5" />
          <el-option label="余额支付消费" value="6" />
          <el-option label="收益转入余额" value="7" />
        </el-select>
      </div>

      <el-table v-loading="loading" :data="logs" style="width: 100%">
        <el-table-column label="变动类型" width="120">
          <template slot-scope="scope">
            <el-tag :type="typeTag(scope.row.type)" size="small">{{ typeText(scope.row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="变动金额（元）" width="140">
          <template slot-scope="scope">
            <span :class="Number(scope.row.amount) >= 0 ? 'amount-in' : 'amount-out'">
              {{ scope.row.amountStr }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="可提现余额（元）" width="150">
          <template slot-scope="scope">{{ scope.row.balanceAfterStr }}</template>
        </el-table-column>
        <el-table-column label="关联单号" prop="refNo" width="200" />
        <el-table-column label="解冻时间" width="170">
          <template slot-scope="scope">
            <template v-if="Number(scope.row.type) === 1">
              <el-tag v-if="Number(scope.row.incomeStatus) === 2" type="success" size="mini">已解冻</el-tag>
              <span v-else-if="scope.row.unfreezeTime">{{ formatTime(scope.row.unfreezeTime) || '-' }}</span>
              <span v-else>-</span>
            </template>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="说明" prop="remark" min-width="240" show-overflow-tooltip />
        <el-table-column label="时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && logs.length === 0" class="empty-box">
        <i class="el-icon-wallet empty-icon"></i>
        <p>还没有收益记录，插件售出后这里会显示收益明细</p>
      </div>

      <el-pagination
        v-if="total > 0"
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page.sync="queryParams.pageNum"
        :page-size="queryParams.pageSize"
        @current-change="loadLogs" />

      <!-- 收益转入消费余额（单向，不可逆） -->
      <el-dialog title="收益转入消费余额"
                 :visible.sync="transferOpen"
                 width="460px"
                 :close-on-click-modal="false"
                 @close="resetTransferForm">
        <el-alert type="warning"
                  :closable="false"
                  show-icon
                  title="转入的消费余额仅可用于购买插件等消费，不可提现、不可转回可提现余额，请谨慎操作。" />
        <el-form ref="transferForm"
                 :model="transferForm"
                 :rules="transferRules"
                 label-width="110px"
                 style="margin-top:16px">
          <el-form-item label="可转入金额">
            <span class="transfer-available">￥{{ income.incomeBalance || '0.00' }} 元</span>
          </el-form-item>
          <el-form-item label="转入金额（元）" prop="amountYuan">
            <el-input v-model="transferForm.amountYuan" placeholder="请输入转入金额，最多两位小数" />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="transferOpen = false">取 消</el-button>
          <el-button type="primary" :loading="transferLoading" @click="submitTransfer">确认转入</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { getMyIncome, listMyIncomeLogs, transferIncomeToBalance } from '@/api/developer'
import { dateFormat } from '@/utils/common'

export default {
  name: 'devIncome',
  data () {
    return {
      loading: true,
      income: {},
      stat: {},
      logs: [],
      total: 0,
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        type: ''
      },
      transferOpen: false, // 转入消费余额弹窗
      transferLoading: false,
      transferForm: {
        amountYuan: ''
      },
      transferRules: {
        amountYuan: [
          { required: true, message: '请输入转入金额', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '金额只能为数字，最多两位小数', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    formatTime (value) {
      return value ? dateFormat(value, 'YYYY-MM-DD HH:mm') : '-'
    },
    typeText (type) {
      const map = { 1: '订单收益', 2: '冻结解冻', 3: '提现申请', 4: '提现完成', 5: '提现退回', 6: '余额支付消费', 7: '收益转入余额' }
      return map[Number(type)] || '其他'
    },
    typeTag (type) {
      const map = { 1: 'success', 2: 'primary', 3: 'warning', 4: 'info', 5: 'danger', 6: 'info', 7: 'success' }
      return map[Number(type)] || 'info'
    },
    // 打开转入消费余额弹窗
    openTransfer () {
      if (Number(this.income.incomeBalance || 0) <= 0) {
        this.$message.warning('当前没有可转入的收益余额')
        return false
      }
      this.transferOpen = true
    },
    resetTransferForm () {
      this.transferForm.amountYuan = ''
      this.$refs.transferForm && this.$refs.transferForm.clearValidate()
    },
    // 提交转入：转入消费余额不可提现，二次确认后提交
    submitTransfer () {
      this.$refs.transferForm.validate(valid => {
        if (!valid) {
          return false
        }
        const amountYuan = Number(this.transferForm.amountYuan)
        const amountFen = Math.round(amountYuan * 100)
        if (!(amountFen > 0)) {
          this.$message.error('转入金额必须大于 0')
          return false
        }
        if (amountFen > Math.round(Number(this.income.incomeBalance || 0) * 100)) {
          this.$message.error('转入金额不能超过可提现余额')
          return false
        }
        this.$confirm('转入消费余额不可提现，是否确认转入？', '温馨提示', {
          confirmButtonText: '确认转入',
          cancelButtonText: '再想想',
          type: 'warning'
        }).then(() => {
          this.transferLoading = true
          transferIncomeToBalance({ amount: amountFen }).then(res => {
            const { code, data, msg } = res
            if (code === 0) {
              this.$message.success((data && data.msg) || '转入成功')
              this.transferOpen = false
              this.loadIncome()
              this.loadLogs()
            } else {
              this.$message.error(msg || '转入失败')
            }
          }).finally(() => {
            this.transferLoading = false
          })
        }).catch(() => {})
      })
    },
    loadIncome () {
      getMyIncome().then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.income = data || {}
          this.stat = (data && data.stat) || {}
        } else {
          this.$message.error(msg || '获取收益信息失败')
        }
      })
    },
    loadLogs () {
      this.loading = true
      listMyIncomeLogs(this.queryParams).then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.logs = data.list || []
          this.total = data.total || 0
        } else {
          this.$message.error(msg || '获取收益流水失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParams.pageNum = 1
      this.loadLogs()
    },
    goWithdraw () {
      this.$router.push({ path: 'developerWithdraw' })
    }
  },
  created () {
    this.loadIncome()
    this.loadLogs()
  }
}
</script>

<style lang="less" scoped>
.dev-income {
  .panel-heading h2 {
    margin: 0 0 20px 0;
    font-size: 22px;
    color: #303133;
  }

  .stat-row {
    margin-bottom: 12px;
  }

  .stat-card {
    padding: 20px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    text-align: center;

    &.primary {
      background: linear-gradient(135deg, #f0f9ff 0%, #e6f4ff 100%);
      border-color: #b3d8ff;
    }

    .stat-label {
      margin: 0;
      font-size: 13px;
      color: #909399;
    }

    .stat-num {
      margin: 10px 0;
      font-size: 28px;
      font-weight: 600;
      color: #303133;
      font-family: 'SF Mono', Consolas, Menlo, monospace;

      &.warning {
        color: #e6a23c;
      }
    }

    .stat-actions {
      margin-top: 4px;

      .el-button {
        margin: 0 4px;
      }
    }

    .stat-sub {
      margin: 0;
      font-size: 12px;
      color: #909399;
    }
  }

  .mini-card {
    padding: 12px 16px;
    background: #f5f7fa;
    border-radius: 4px;
    font-size: 13px;
    color: #606266;

    b {
      color: #303133;
    }
  }

  .alert-info-light {
    background-color: #d9edf7;
    border-color: #c6e4f3;
    color: #3a87ad;
    padding: 15px;
    margin: 16px 0;
    border: 1px solid transparent;
    border-radius: 3px;
    font-size: 13px;
    line-height: 22px;
  }

  .filter-bar {
    margin-bottom: 12px;
  }

  .amount-in {
    color: #67c23a;
    font-weight: 600;
  }

  .transfer-available {
    color: #67c23a;
    font-weight: 600;
  }

  .amount-out {
    color: #f56c6c;
    font-weight: 600;
  }

  .empty-box {
    padding: 50px 0;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 48px;
      color: #c0c4cc;
    }

    p {
      margin: 14px 0 0 0;
    }
  }

  .pager {
    margin-top: 18px;
    text-align: right;
  }
}
</style>
